import React from 'react';
import { CheckIcon, InfoIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { DisclosureBadgeGroup } from './DisclosureBadgeGroup';
import { DISCLOSURE_ANSWER_OPTIONS, DISCLOSURE_QUESTION, DISCLOSURE_QUESTION_EXPLANATION, disclosuresFromAnswers, type DisclosureAnswerId, type DisclosureSubjectType } from '../../data/disclosures';
interface DisclosureQuestionProps {
  value: DisclosureAnswerId[];
  onChange: (answers: DisclosureAnswerId[]) => void;
  subjectType: DisclosureSubjectType;
}

/**
 * The submission-time disclosure question, reusable across review and upload
 * flows.
 *
 * This is deliberately STRUCTURAL rather than asking creators to type #gifted
 * or #ad into their body text: because the answer lives on the record, the
 * resulting badge renders consistently, can be filtered on, and cannot be lost
 * in prose.
 */
export const DisclosureQuestion: React.FC<DisclosureQuestionProps> = ({
  value,
  onChange,
  subjectType
}) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const toggle = (id: DisclosureAnswerId) => {
    // "No" and any commercial answer are mutually exclusive — selecting one
    // clears the other, so a contradictory pair can never be submitted.
    if (id === 'none') {
      onChange(value.includes('none') ? [] : ['none']);
      return;
    }
    const withoutNone = value.filter(answer => answer !== 'none');
    onChange(withoutNone.includes(id) ? withoutNone.filter(answer => answer !== id) : [...withoutNone, id]);
  };
  const preview = disclosuresFromAnswers(value, subjectType);
  return <fieldset className={`rounded-lg border p-4 ${dk.borderSubtle} ${dk.surfaceMuted}`} data-id="element-1517" data-mp="2d2489ngjrjon">
      <legend className={`px-1 text-sm font-medium ${dk.textHeading}`} data-id="element-1518" data-mp="3sil59aahyrms">
        {DISCLOSURE_QUESTION}
      </legend>

      <div className="flex flex-wrap gap-2 mt-2" data-id="element-1519" data-mp="3oghhbm6n5ukm">
        {DISCLOSURE_ANSWER_OPTIONS.map(option => {
        const isSelected = value.includes(option.id);
        return <button key={option.id} type="button" onClick={() => toggle(option.id)} aria-pressed={isSelected} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full border min-h-[36px] transition-colors ${isSelected ? `${rt.bgMedium} ${rt.textPrimary} border-current` : `${dk.surface} ${dk.textSecondary} ${dk.isDark ? 'border-gray-700' : 'border-gray-300'}`}`} data-id="element-1520" data-mp="2q56rukuq2h9a">
              {isSelected && <CheckIcon className="h-3.5 w-3.5" data-id="element-1521" data-mp="2bfio7cz2j6ie" />}
              {option.label}
            </button>;
      })}
      </div>

      <p className={`flex gap-2 mt-3 text-xs leading-relaxed ${dk.textMuted}`} data-id="element-1522" data-mp="1jpw2sipcvbss">
        <InfoIcon className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-1523" data-mp="1jntec33upqq6" />
        {DISCLOSURE_QUESTION_EXPLANATION}
      </p>

      {/* Show exactly what will appear publicly, so nothing is a surprise. */}
      {preview.length > 0 && <div className={`mt-3 pt-3 border-t ${dk.borderSubtle}`} data-id="element-1524" data-mp="1b9otazwrppxp">
          <div className="flex flex-wrap items-center gap-2" data-id="element-1525" data-mp="176s2wn0zkpj7">
            <span className={`text-xs ${dk.textSecondary}`} data-id="element-1526" data-mp="1757b35623kp1">
              Will be shown as:
            </span>
            <DisclosureBadgeGroup disclosures={preview} subjectType={subjectType} maxVisible={4} showIcon={false} data-id="element-1527" data-mp="12aa21y8s1bc5" />
          </div>
        </div>}
      {preview.length === 0 && value.includes('none') && <p className={`mt-3 pt-3 border-t text-xs ${dk.borderSubtle} ${dk.textMuted}`} data-id="element-1528" data-mp="njawyx75h4xb">
          No disclosure badge will be shown.
        </p>}
    </fieldset>;
};
export default DisclosureQuestion;